'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	SpotMarketAccount,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import {
	abbreviateAddress,
	createTokenAccountIx,
	getTokenAddressForDepositAndWithdraw,
} from '@velocity-exchange/common';
import { createTransferCheckedInstruction } from '@solana/spl-token';
import { PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import { useEffect, useMemo, useState } from 'react';
import { MIN_LEFTOVER_SOL } from 'src/constants/constants';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import useCurrentWalletCollateralBalance from 'src/hooks/useCurrentWalletCollateralBalance';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import { CollateralInputV2 } from '../Inputs/CollateralInput';
import Input from 'src/components/primitives/Input/Input';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const SendFromWalletModal = () => {
	const { hide } = useModal();
	const spotMarkets = useSpotMarkets();

	// Move SOL to the top of list
	const CurrentBanks = useMemo(
		() =>
			[...spotMarkets].sort((a, b) => {
				if (a.symbol === 'SOL' && b.symbol !== 'SOL') {
					return -1;
				} else if (a.symbol !== 'SOL' && b.symbol === 'SOL') {
					return 1;
				}
				return 0;
			}),
		[spotMarkets]
	);

	const [submitting, setSubmitting] = useState(false);
	const [destinationAddress, setDestinationAddress] = useState('');
	const [destinationPublicKey, setDestinationPublicKey] =
		useState<PublicKey | null>(null);
	const [destinationAddressIsValid, setDestinationAddressIsValid] =
		useState(false);
	const [transferAmount, setTransferAmount] = useState('');
	const [selectedBank, setSelectedBank] = useState<SpotMarketConfig>(
		CurrentBanks[0]
	);
	const [allowedMaxSend, setAllowedMaxSend] = useState(false);
	const [connection] = useVelocityStore((s) => [
		s.connection?.current,
		s.velocityClient?.client,
	]);
	const wallet = useCurrentWalletAdapter();
	const velocityClient = useVelocityClient();

	const [currentBalance] = useCurrentWalletCollateralBalance(selectedBank);
	const [transferableBalance] =
		useTransferableWalletCollateralBalance(selectedBank);
	const transferAmountBigNum = parseInputBigNumOrZero(
		transferAmount ? transferAmount : '0',
		selectedBank.precisionExp
	);
	const exceededMax = transferAmountBigNum.gt(
		allowedMaxSend ? currentBalance : transferableBalance
	);

	const isMaxSolTransfer =
		transferAmount !== '' &&
		transferAmountBigNum.eq(
			allowedMaxSend ? currentBalance : transferableBalance
		) &&
		selectedBank.symbol === 'SOL';

	const allowConfirmation =
		!exceededMax &&
		destinationAddressIsValid &&
		!submitting &&
		transferAmount &&
		transferAmountBigNum.gt(BigNum.zero(BASE_PRECISION_EXP), true);

	useEffect(() => {
		if (exceededMax) {
			const maxAmount = allowedMaxSend ? currentBalance : transferableBalance;
			setTransferAmount(maxAmount.print());
		}
	}, [exceededMax, allowedMaxSend, currentBalance, transferableBalance]);

	const handleMaxBypass = () => {
		setAllowedMaxSend(true);
		setTransferAmount(currentBalance?.print() ?? '');
	};

	const handleChangeTransferAmount = (newValue?: string) => {
		setTransferAmount(newValue ?? '');
	};

	const handleChangeAssetType = (bank: SpotMarketConfig) => {
		setTransferAmount('');
		setSelectedBank(bank);
	};

	const handleChangeDestinationAddress = (str: string) => {
		setDestinationAddress(str);
		try {
			const dstPublicKey = new PublicKey(str);
			setDestinationPublicKey(dstPublicKey);
			setDestinationAddressIsValid(true);
		} catch {
			setDestinationPublicKey(null);
			setDestinationAddressIsValid(false);
		}
	};

	const handleSend = async () => {
		if (!connection || !wallet.publicKey || !destinationPublicKey) return;
		setSubmitting(true);

		const toastId = `transfer-${transferAmount}-${
			selectedBank.symbol
		}-${destinationAddress}-${Date.now()}`;

		const abbreviatedDst = abbreviateAddress(destinationPublicKey);

		notify({
			type: 'awaiting_wallet_confirmation',
			id: toastId,
			showUntilCancelled: true,
		});

		try {
			const { blockhash } = await connection.getLatestBlockhash('finalized');

			const sendTx = new Transaction({
				feePayer: wallet.publicKey,
				recentBlockhash: blockhash,
			});

			const transferAmountBase = transferAmountBigNum
				.shiftTo(selectedBank.precisionExp)
				.toString();

			if (selectedBank.symbol === 'SOL') {
				sendTx.add(
					SystemProgram.transfer({
						fromPubkey: wallet.publicKey,
						toPubkey: destinationPublicKey,
						lamports: Number(transferAmountBase),
					})
				);
			} else {
				const destAtaPublicKey = await getTokenAddressForDepositAndWithdraw(
					velocityClient?.getSpotMarketAccount(
						selectedBank.marketIndex
					) as SpotMarketAccount,
					destinationPublicKey
				);

				const sourceAtaPublicKey = await getTokenAddressForDepositAndWithdraw(
					velocityClient?.getSpotMarketAccount(
						selectedBank.marketIndex
					) as SpotMarketAccount,
					wallet.publicKey
				);

				let destAtaExists = false;
				try {
					const destAtaInfo = await connection.getAccountInfo(destAtaPublicKey);
					destAtaExists = !!destAtaInfo;
				} catch {
					destAtaExists = false;
				}

				if (!destAtaExists) {
					sendTx.add(
						await createTokenAccountIx(
							destinationPublicKey,
							selectedBank.mint,
							wallet.publicKey
						)
					);
				}

				const transferCheckedIx = createTransferCheckedInstruction(
					sourceAtaPublicKey,
					selectedBank.mint,
					destAtaPublicKey,
					wallet.publicKey,
					Number(transferAmountBase),
					selectedBank.precisionExp.toNumber()
				);

				sendTx.add(transferCheckedIx);
			}

			const signed = await wallet.signTransaction(sendTx);

			notify({
				type: 'awaiting',
				updatePrevious: true,
				message: `Sending ${transferAmount} ${selectedBank.symbol} to ${abbreviatedDst}.`,
				id: toastId,
				showUntilCancelled: true,
			});

			const signature = await connection.sendRawTransaction(signed.serialize());

			const confirmation = await connection.confirmTransaction(
				signature,
				'finalized'
			);

			if (confirmation.value.err) {
				notify({
					type: 'error',
					updatePrevious: true,
					message: `Transaction failed`,
					description: `${confirmation.value.err}`,
					id: toastId,
				});
				setSubmitting(false);
				return;
			}

			notify({
				type: 'success',
				updatePrevious: true,
				message: `Sent ${transferAmount} ${selectedBank.symbol} to ${abbreviatedDst}.`,
				id: toastId,
				action: { type: 'txnLink', txnSig: signature },
			});
			setSubmitting(false);
			hide();
		} catch (error) {
			notify({
				type: 'error',
				updatePrevious: true,
				message: `Error sending ${selectedBank.symbol}`,
				description: `${error}`,
				id: toastId,
			});
			setSubmitting(false);
		}
	};

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="send-from-wallet-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="send-from-wallet-modal-close">
				Send Assets
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<Input
					id="destination-address"
					type="text"
					labelVariant="standard"
					label="Send to"
					value={destinationAddress}
					onChange={handleChangeDestinationAddress}
					placeholder="Enter a Solana address"
				/>

				{destinationAddress.length > 0 && !destinationAddressIsValid && (
					<InlineAlert variant="error" title="Invalid Solana address" />
				)}

				<CollateralInputV2
					selectedMarket={selectedBank}
					maxAmount={allowedMaxSend ? currentBalance : transferableBalance}
					onChangeMarket={handleChangeAssetType}
					label="Asset and amount"
					value={transferAmount}
					onChangeValue={handleChangeTransferAmount}
					source={{ type: 'wallet' }}
				/>

				{isMaxSolTransfer &&
					(allowedMaxSend ? (
						<InlineAlert
							variant="warning"
							title="You are transferring out your entire SOL balance, so you will not be able to make transactions after sending."
						/>
					) : (
						<p>
							We will leave {MIN_LEFTOVER_SOL.toFixed(2)} SOL in your wallet to
							cover future transaction fees.{' '}
							<TextButton
								size="sm"
								variant="tertiary"
								onClick={handleMaxBypass}
							>
								Bypass
							</TextButton>
						</p>
					))}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					className="w-full"
					disabled={!allowConfirmation}
					onClick={handleSend}
					isLoading={submitting}
					dataTestId="send-from-wallet-modal-submit"
				>
					Send {transferAmount || '0'} {selectedBank.symbol}
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

export default SendFromWalletModal;
